/**
 * Display self-recovery.
 *
 * A signage screen runs unattended for weeks, so any failure that stops the
 * content from painting is permanent until someone physically restarts it.
 *
 * IMPORTANT: automatic reloading is DISABLED by default in this release.
 * requestRecovery() always REPORTS the fault to Sentry; it only reloads when
 * recovery has been explicitly enabled. That way production gains full
 * visibility with no behavioural change, and reloading can be switched on once
 * the reports show what is actually firing in the field.
 *
 * Enable per screen (or fleet-wide via a future setting):
 *     localStorage.setItem('display_app_recovery_enabled', '1')
 * Disable again:
 *     localStorage.removeItem('display_app_recovery_enabled')
 */

import { captureMessage } from '@sentry/browser';

const ENABLE_KEY = 'display_app_recovery_enabled';
const RELOAD_LOG_KEY = 'display_app_recovery_log';

const MIN_UPTIME_MS = 2 * 60 * 1000; // never reload while still starting up / buffering
const RELOAD_WINDOW_MS = 60 * 60 * 1000;
const MAX_RELOADS_PER_WINDOW = 3; // then stop: reloading cannot fix a broken asset
const MAX_JITTER_MS = 60 * 1000; // stagger the fleet so a CDN blip cannot stampede

const bootedAt = Date.now();
let reloadScheduled = false;

export type ReloadRecord = { at: number; reason: string };

/** Auto-reload is opt-in. Reporting happens either way. */
export function isRecoveryEnabled(): boolean {
  try {
    return window.localStorage.getItem(ENABLE_KEY) === '1';
  } catch {
    return false;
  }
}

function readReloadLog(): ReloadRecord[] {
  try {
    const raw = window.localStorage.getItem(RELOAD_LOG_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map(entry => (typeof entry === 'number' ? { at: entry, reason: 'unknown' } : entry))
      .filter((entry): entry is ReloadRecord => typeof entry?.at === 'number');
  } catch {
    return [];
  }
}

function writeReloadLog(entries: ReloadRecord[]): void {
  try {
    window.localStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or unavailable — recovery must still work.
  }
}

/** Reloads inside the rate-limit window. Also shows the field WHY a screen restarted. */
export function getRecentReloads(): ReloadRecord[] {
  const cutoff = Date.now() - RELOAD_WINDOW_MS;
  return readReloadLog().filter(entry => entry.at >= cutoff);
}

/**
 * Report that the display is stuck, and reload it if recovery is enabled.
 * Returns the reason a reload was declined, or null if one was scheduled.
 */
export function requestRecovery(
  reason: string,
  detail: Record<string, unknown> = {},
  options: { report?: boolean } = {},
): string | null {
  const uptimeSeconds = Math.round((Date.now() - bootedAt) / 1000);
  const enabled = isRecoveryEnabled();

  // Always report, whether or not we are allowed to act on it. Callers that
  // have already sent a richer event (e.g. an exception with a stack) pass
  // report: false so the same fault is not recorded twice.
  if (options.report !== false) {
    try {
      captureMessage(`Display needs recovery: ${reason}`, {
        level: 'error',
        tags: { failureKind: 'needs-recovery', recoveryEnabled: String(enabled) },
        extra: { reason, uptimeSeconds, recentReloads: getRecentReloads().length, ...detail },
      });
    } catch {
      // Never let telemetry stop the recovery path.
    }
  }

  console.warn(`[recovery] ${reason} (uptime ${uptimeSeconds}s, auto-reload ${enabled ? 'enabled' : 'disabled'})`);

  if (!enabled) {
    return 'auto-reload disabled';
  }

  if (reloadScheduled) {
    return 'already scheduled';
  }

  if (Date.now() - bootedAt < MIN_UPTIME_MS) {
    return `too early (uptime ${uptimeSeconds}s)`;
  }

  // Reloading with no network cannot re-fetch the app: every asset is served
  // with `max-age=0, must-revalidate`, so an offline reload would replace a
  // white screen with a dead one. Wait and let the next report try again.
  if (navigator.onLine === false) {
    return 'offline — deferring';
  }

  const recent = getRecentReloads();
  if (recent.length >= MAX_RELOADS_PER_WINDOW) {
    return `rate limited (${recent.length} reloads in the last hour)`;
  }

  reloadScheduled = true;
  writeReloadLog([...recent, { at: Date.now(), reason }]);

  window.setTimeout(() => {
    // Plain reload only. Never the forceDisconnect path, which clears the
    // deviceId and would unpair the screen.
    window.location.reload();
  }, Math.floor(Math.random() * MAX_JITTER_MS));

  return null;
}
